/**
 * Garden Art ERP design tokens.
 *
 * Strategy:
 *  - AntD v5 defaults are the baseline (ASM-006 — no custom brand theming for S1).
 *  - These tokens EXTEND AntD rather than replace it. Where a token overlaps
 *    with AntD's theme token (e.g. `colorPrimary`, `borderRadius`, `sizeMD`),
 *    prefer the AntD token in AntD components and use our tokens for bespoke
 *    elements + non-AntD surfaces (chart legends, custom cards, status chips).
 *  - Spacing uses a 4-px base scale that lines up with AntD's `sizeXXS..sizeXXL`.
 *  - Role-accent colors are ONLY for role-specific headers / avatar chips /
 *    dashboard hero strips — never for status semantics.
 *
 * Rule of thumb:
 *  - Use an AntD token when styling an AntD component's theme via ConfigProvider.
 *  - Use a `token.*` here when styling a plain div, custom chart element, or
 *    bespoke wrapper component.
 *  - Use the `semantic.status.*` tokens for Draft/Submitted/Approved/etc.
 *    NEVER re-derive those colors from AntD primitives inline.
 */

// ---- Spacing (4px base) ---------------------------------------------------

export const spacing = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

export type SpacingKey = keyof typeof spacing;

// ---- Typography scale -----------------------------------------------------

export const typography = {
  // Self-hosted via @fontsource. System-font fallbacks remain so the page
  // still renders before the webfont arrives.
  fontFamilyLtr:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  fontFamilyRtl:
    "'Tajawal', 'Segoe UI Arabic', 'Noto Kufi Arabic', 'Noto Naskh Arabic', Tahoma, sans-serif",
  // Size scale — mirrors AntD's type-scale so headers feel native.
  size: {
    xs: 12,
    sm: 13,
    base: 14, // AntD default body
    md: 16,
    lg: 18,
    xl: 20,
    heading4: 20,
    heading3: 24,
    heading2: 30,
    heading1: 38,
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    base: 1.5715, // AntD default
    relaxed: 1.75,
  },
} as const;

// ---- Radius ---------------------------------------------------------------

export const radius = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 6, // AntD default
  lg: 8,
  xl: 12,
  pill: 999,
} as const;

// ---- Elevation ------------------------------------------------------------

export const elevation = {
  // Mirrors AntD's `boxShadowSecondary / boxShadowTertiary` intent.
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 2px 8px rgba(0, 0, 0, 0.08)',
  lg: '0 6px 16px rgba(0, 0, 0, 0.12)',
  xl: '0 12px 24px rgba(0, 0, 0, 0.16)',
} as const;

// ---- Semantic colors ------------------------------------------------------

// Use for status chips, alerts, KPI deltas. Do not re-derive inline.
//
// Garden Art brand palette (post-S3 polish, replacing AntD defaults per
// ASM-006 turn-on). Greens dominate (landscape/horticulture identity);
// warm gold accents pull from Najdi/Diriyah heritage architecture.
export const semantic = {
  brand: {
    primary: '#1F4D3A', // Evergreen — primary actions, active nav
    primaryHover: '#2A6149',
    primaryActive: '#173B2C',
    secondary: '#D9C7A0', // Najdi sand — warm secondary surface
    accent: '#B8893E', // Najdi gold — hero accents, derived-value chips
    accentSoft: '#E0C58A',
  },
  status: {
    neutral: '#8C8C8C',
    info: '#3A6EA8', // Desaturated steel-blue (was #1677ff) — calmer next to greens
    success: '#3F8E5A', // Garden green — same family as primary, distinct hue
    warning: '#D9941F', // Warm amber — slightly darker than AntD default
    error: '#C2502E', // Terracotta — softer than #FF4D4F, on-brand
    processing: '#1F4D3A', // Use primary green for in-flight states
  },
  // Named module/workflow statuses used across every screen.
  // These are the single source of truth for <StatusPill/>.
  pill: {
    draft: '#8C8C8C',
    submitted: '#3A6EA8',
    underReview: '#7A4DA0',
    approved: '#3F8E5A',
    rejected: '#C2502E',
    inProgress: '#D9941F',
    closed: '#595959',
    paid: '#3F8E5A',
    overdue: '#C2502E',
    won: '#1F4D3A',
    lost: '#BFBFBF',
  },
  // Role accent colors (for role badges + dashboard hero strips). NOT status.
  // Tuned to harmonize with the brand evergreen + Najdi sand. Each role gets
  // a hue family far enough from primary green (#1F4D3A) to read as distinct
  // role identity, but desaturated enough to feel premium not garish.
  role: {
    president: '#3A4A75', // Deep navy — gravitas
    vpOperations: '#6E4F8B', // Muted aubergine
    financeManager: '#1E6F6F', // Deep teal — money/finance trad cue
    engineeringDirector: '#8A4A1F', // Sienna — earth/build
    opsManager: '#B8893E', // Najdi gold — operations/heritage
    contractsAdmin: '#9C3961', // Garnet — contracts gravitas
    marketingManager: '#3F8E5A', // Garden green — growth/leads
    hrManager: '#3A6EA8', // Steel blue — people/admin
    bilingualViewer: '#8C8C8C',
  },
} as const;

export type PillStatus = keyof typeof semantic.pill;
export type RoleAccent = keyof typeof semantic.role;

// ---- Surface colors (neutral backgrounds) --------------------------------

export const surface = {
  page: '#FAF8F4', // Warm off-white — feels less corporate than #f5f5f5
  card: '#FFFFFF',
  muted: '#F4F0E8', // Soft sand wash for subtle sections
  headerDark: '#0F2A20', // Deep evergreen for the top header (was AntD #001529 navy)
  railDark: '#15382B', // Slightly lighter for the primary rail
  border: '#D9CFC0', // Warm border tone (was cool #d9d9d9)
  borderSubtle: '#EFE9DD',
  textPrimary: '#1A1F1B', // Slightly warm dark — not pure black
  textSecondary: 'rgba(26, 31, 27, 0.72)',
  textTertiary: 'rgba(26, 31, 27, 0.52)',
  textDisabled: 'rgba(26, 31, 27, 0.28)',
} as const;

// ---- Z-index layering ----------------------------------------------------

export const zIndex = {
  base: 0,
  sticky: 10,
  header: 50,
  dropdown: 1050,
  modal: 1000,
  notification: 1010,
  tooltip: 1070,
} as const;

// ---- Motion --------------------------------------------------------------

export const motion = {
  duration: {
    fast: '120ms',
    base: '200ms',
    slow: '320ms',
  },
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  },
} as const;

// ---- Aggregate ------------------------------------------------------------

export const tokens = {
  spacing,
  typography,
  radius,
  elevation,
  semantic,
  surface,
  zIndex,
  motion,
} as const;

export type Tokens = typeof tokens;
